import React from "react";

import {
	Container,
	CssBaseline,
} from "@mui/material";
import Box from "@mui/material/Box";

import { t } from "@lingui/macro";
import Keycloak, { KeycloakInstance } from "keycloak-js";
import {
	RouteComponentProps,
	withRouter,
} from "react-router-dom";

import {
	ErrorDialog,
	Loading,
	NetworkService,
	NumberUtils,
} from "@sinossi/mates-react-library";

import {
	Footer,
	Header
} from "structure";
import { KeycloakError } from "pages/errors";
import Routes from "./routes";

type AppProps = {} & RouteComponentProps

interface AppState {
	authenticated: boolean,
	keycloak: KeycloakInstance | null,
	authenticationError: boolean,
	keycloakInitError: string | null,
	errorDialog: {
		open: boolean, title: string, message: string,
	},
}

class App extends React.Component<AppProps, AppState> {
	constructor(props: any) {
		super(props);
		this.state = {
			authenticated: false,
			keycloak: null,
			authenticationError: false,
			keycloakInitError: null,
			errorDialog: {
				open: false,
				title: "",
				message: "",
			},
		};
	}

	componentDidMount() {

		const config = {
			url: process.env.REACT_APP_KEYCLOAK_URL as string,
			realm: process.env.REACT_APP_KEYCLOAK_REALM as string,
			clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID as string,
		};

		const keycloak = new Keycloak(config);

		keycloak
			.init({ onLoad: "check-sso" })
			.then((authenticated: boolean) => this.onLoginRequired(keycloak, authenticated))
			.catch(reason => {
				console.error("Cannot init keycloak");
				console.error(reason);

				let errorMessage: string | null = null;
				if(reason) {
					if(reason instanceof Error) {
						errorMessage = reason.message;
					} else if(typeof reason === "string") {
						errorMessage = reason;
					} else if(typeof reason === "object" && "message" in (reason as Record<string, unknown>)) {
						const maybeMessage = (reason as Record<string, unknown>).message;
						errorMessage = typeof maybeMessage === "string" ? maybeMessage : null;
					}
				}

				this.setState(() => ({
					keycloak: keycloak,
					authenticated: false,
					authenticationError: true,
					keycloakInitError: errorMessage,
				}));

				NetworkService.removeAuthenticationToken();

				this.props.history.push("/keycloak-error");
			});

		keycloak.onTokenExpired = () => this.onTokenExpired(keycloak);
	}

	private onLoginRequired = (keycloak: KeycloakInstance, authenticated: boolean) => {

		NetworkService.saveAuthenticationToken(keycloak.token || "");
		this.setState(() => ({
			keycloak: keycloak,
			authenticated: authenticated,
			authenticationError: false,
			keycloakInitError: null,
		}));

	};

	private onTokenExpired = (keycloak: KeycloakInstance) => {

		const keycloakTokenValidity: number = NumberUtils.toInteger(
			process.env.REACT_APP_KEYCLOAK_TOKEN_VALIDITY as string, 30);

		keycloak
			.updateToken(keycloakTokenValidity)
			.then(() => {
				//console.log("Successfully get a new token", keycloak.token);
				NetworkService.saveAuthenticationToken(keycloak.token || "");
			})
			.catch(() => {
				console.log("error refreshing token.");
				this.setState(() => ({
					errorDialog: {
						open: true,
						title: t({
							id: "auth.error.title",
							message: "Errore di autenticazione",
						}),
						message: t({
							id: "auth.error.cannot_refresh",
							message: "La sessione è scaduta, effettuare nuovamente l’autenticazione",
						}),
					},
				}));
			});
	};

	private onErrorDialogClose = (): void => {
		this.setState(() => ({
			errorDialog: {
				open: false,
				title: "",
				message: "",
			}
		}));
		this.state.keycloak?.logout();
	};

	checkIsAPageWithHeader = () => {
		const ROUTES_WITHOUT_HEADER = [
			"/",
			"/keycloak-error"
		];

		let returnValue = !ROUTES_WITHOUT_HEADER.includes(this.props.location.pathname);

		returnValue = returnValue && this.checkDontStartsWith(ROUTES_WITHOUT_HEADER, this.props.location.pathname);

		return returnValue;
	};

	checkIsUnauthenticatedRoute = () => {
		const UNAUTHROUTES = [
			"/",
			"/end",
			"/privacy-agreement",
			"/forbidden",
			"/identification-success",
			"/keycloak-error"
		];

		let returnValue = !UNAUTHROUTES.includes(this.props.location.pathname);

		returnValue = returnValue && this.checkDontStartsWith(UNAUTHROUTES, this.props.location.pathname);

		return returnValue;
	};

	private checkDontStartsWith = (routeUrl: Array<string>, pathname: string) => {
		let result = true;

		for (let el of routeUrl) {
			if (pathname.startsWith(el) && el !== "/") {
				result = false;
			}
		}

		return result;
	};

	private getBackgroundClassBasedOnEnv(): string {

		if ((process.env.REACT_APP_PRODUCT_NAME || "").includes("LawaL")) {
			return "lawalBackgroundClass";
		}


		if ((process.env.REACT_APP_PRODUCT_NAME || "").includes("mgi")) {
			return "mgiBackgroundClass";
		}

		return "standardBackgroundClass";
	}

	public render() {
		let displayHeader = this.checkIsAPageWithHeader();

		let unauthenticatedRoute = this.checkIsUnauthenticatedRoute();

		if(this.state.authenticationError) {
			displayHeader = false;
		}

		const shouldRenderRoutes = Boolean(this.state.keycloak) && !this.state.authenticationError;

		return (<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100vh",
			}}
			className={`${(this.props.location.pathname === "/") ? this.getBackgroundClassBasedOnEnv() : ""}`}>
			<CssBaseline />
			<Box sx={{ display: "flex" }}>
				{displayHeader &&
					<Header keycloak={this.state.keycloak} unauthenticatedRoute={!unauthenticatedRoute} />}
				<Container component="main"
					sx={{
						flexGrow: 1,
						p: 3,
					}}>
					<Box
						sx={{
							bgcolor: "background.paper",
							mb: 8,
							mt: 8,
						}}>
						{!shouldRenderRoutes && this.state.authenticationError && <KeycloakError details={this.state.keycloakInitError} />}
						{!shouldRenderRoutes && !this.state.authenticationError && <Loading show={true} />}
						{shouldRenderRoutes && this.state.keycloak && <Routes keycloak={this.state.keycloak} />}
					</Box>
				</Container>
			</Box>
			<Footer />
			<ErrorDialog
				ok={this.onErrorDialogClose}
				{...this.state.errorDialog} />
		</Box>);
	}

}

export default withRouter(App);
